/** Franchisee adoption-flow API (E3). Mirrors the AdoptService shapes; all calls via networkManager. */
import { networkManager } from './NetworkManager';

interface ApiResponse<T> {
  status: boolean;
  data: T;
}

export interface AdoptProduct {
  code: string;
  name: string;
  description: string;
  billingType: 'fixed' | 'per_store' | 'per_transaction' | 'included' | 'manual';
  billingFrequency: 'monthly' | 'annual' | 'one_time';
  basePrice: number;
  dependsOn: string[];
  acknowledgments: string[];
  status: string;
}

export interface ContentClause {
  id: string;
  title: string;
  body: string;
}

export interface ProductOption {
  product: AdoptProduct;
  selectable: boolean;
  disabledReason?: string;
  unitPrice: number;
  content?: { version: number; msaRef?: string; sowRef?: string; clauses: ContentClause[] };
}

export interface AdoptFranchisee {
  id: string;
  name: string;
  brand: string;
  bmu: string;
  country: string;
  storeCount: number;
  billingStoreCount: number;
  locale: string;
}

/** Minimal franchisee shape for the self-select picker — no contact or store/financial data of other
 * entities is exposed (mirrors the API's EligibleFranchisee projection). */
export interface EligibleFranchisee {
  id: string;
  name: string;
  country: string;
}

export interface ResponseDetails {
  signerName: string;
  signerTitle: string;
  signerEmail: string;
  noticeAddress?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
}

export interface AdoptionResponse {
  franchiseeId: string;
  linkId: string;
  seq: number;
  status: 'InProgress' | 'Accepted' | 'Declined';
  selectedProducts: string[];
  /** Product display names for `selectedProducts` (joined server-side for My Adoptions). */
  selectedProductNames?: string[];
  details?: ResponseDetails;
  snapshotKey?: string;
  declineReason?: string;
  acceptedAt?: string;
  declinedAt?: string;
  updatedAt: string;
}

export interface AdoptView {
  link: { linkId: string; brand: string; bmu: string; country?: string; acceptanceMode: 'combined' | 'per_product' };
  franchisee: AdoptFranchisee;
  products: ProductOption[];
  response: AdoptionResponse;
  details: ResponseDetails;
  /** Whether to render per-product prices in the flow (portal setting; off by default). */
  showPrices: boolean;
  /** Whether to render per-product descriptions in the flow (portal setting; on by default). */
  showDescriptions: boolean;
}

export type AdoptStage =
  | { stage: 'view'; view: AdoptView; internalRole?: string }
  /** `searchMode` = the admin-configured picker mode: `eligible` ships empty and the client searches
   * via searchAdoptFranchisees (≥3 characters) instead of receiving the market roster. */
  | { stage: 'select'; eligible: EligibleFranchisee[]; searchMode?: boolean; brand?: string; internalRole?: string }
  | { stage: 'no-eligible'; internalRole?: string }
  | { stage: 'unavailable' }
  | { stage: 'ineligible'; internalRole?: string };

export interface AgreementLine {
  productCode: string;
  productName: string;
  billingType: string;
  billingFrequency: string;
  unitPrice: number;
  storeCount: number;
  lineTotal: number;
  contentVersion: number;
  msaRef?: string;
  sowRef?: string;
  clauses: ContentClause[];
}

export interface AgreementSnapshot {
  franchiseeId: string;
  franchiseeName: string;
  linkId: string;
  scope: string;
  brand: string;
  bmu: string;
  country?: string;
  locale: string;
  storeCount: number;
  billingStoreCount: number;
  signer: ResponseDetails;
  lines: AgreementLine[];
  acceptedAt: string;
  hash: string;
}

export interface AcceptInput {
  selectedProducts: string[];
  details: ResponseDetails;
  authorityConfirmed: boolean;
  acknowledgedProducts: string[];
}

export async function resolveLink(linkId: string): Promise<{ available: boolean; brand?: string }> {
  const res = await networkManager.get<ApiResponse<{ available: boolean; brand?: string }>>(`/api/adopt/${linkId}/resolve`);
  return res.data;
}

export async function getAdoptStage(linkId: string): Promise<AdoptStage> {
  const res = await networkManager.get<ApiResponse<AdoptStage>>(`/api/adopt/${linkId}`);
  return res.data;
}

/** Picker search mode: find the caller's entity in the link's market (server requires ≥3 characters). */
export async function searchAdoptFranchisees(linkId: string, q: string): Promise<EligibleFranchisee[]> {
  const res = await networkManager.get<ApiResponse<EligibleFranchisee[]>>(
    `/api/adopt/${linkId}/franchisees?q=${encodeURIComponent(q)}`,
  );
  return res.data;
}

export async function selectFranchisee(linkId: string, franchiseeId: string): Promise<AdoptFranchisee> {
  const res = await networkManager.post<ApiResponse<AdoptFranchisee>>(`/api/adopt/${linkId}/select`, { franchiseeId });
  return res.data;
}

/** Clear the saved franchisee association so the flow returns to the picker. */
export async function changeFranchisee(linkId: string): Promise<void> {
  await networkManager.post<ApiResponse<{ ok: true }>>(`/api/adopt/${linkId}/change-franchisee`, {});
}

export async function acceptAdoption(
  linkId: string,
  input: AcceptInput,
): Promise<{ response: AdoptionResponse; snapshot: AgreementSnapshot }> {
  const res = await networkManager.post<ApiResponse<{ response: AdoptionResponse; snapshot: AgreementSnapshot }>>(
    `/api/adopt/${linkId}/accept`,
    input,
  );
  return res.data;
}

export async function declineAdoption(linkId: string, reason?: string): Promise<AdoptionResponse> {
  const res = await networkManager.post<ApiResponse<AdoptionResponse>>(`/api/adopt/${linkId}/decline`, { reason });
  return res.data;
}

export async function getAgreement(linkId: string, seq: number): Promise<AgreementSnapshot> {
  const res = await networkManager.get<ApiResponse<AgreementSnapshot>>(`/api/adopt/${linkId}/agreement/${seq}`);
  return res.data;
}

export async function listMyAdoptions(): Promise<AdoptionResponse[]> {
  const res = await networkManager.get<ApiResponse<AdoptionResponse[]>>(`/api/my-adoptions`);
  return res.data;
}
